import React, { useEffect, useRef, useState } from 'react';

import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@mui/material';
import { MAIN_THEME } from '@styles';

import { IEnhancedMenuItem } from '../table/types';

interface IEnhancedMenuProps {
  title: string;
  items: IEnhancedMenuItem[];
  itemClassName?: string;
}

export const EnhancedMenu: React.FC<IEnhancedMenuProps> = ({
  title,
  items,
  itemClassName
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: any) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  // Close the Popper when scrolling
  useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div>
      <Button
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        classes={{
          root: itemClassName
        }}
      >
        {title}
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        style={{ zIndex: 100 }}
        placement={'bottom-end'}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {items &&
                    items.length > 0 &&
                    items.map(
                      (item) =>
                        !item.hidden && (
                          <MenuItem
                            disabled={item.disabled}
                            key={item.title}
                            onClick={(e) => {
                              item.onClick();
                              handleClose(e);
                            }}
                            disableRipple
                            sx={{
                              fontFamily: MAIN_THEME.font.regular,
                              fontSize: `${MAIN_THEME.fontSize.sm}`
                            }}
                          >
                            {item.title}
                          </MenuItem>
                        )
                    )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default EnhancedMenu;
